import React, { PropsWithChildren, useMemo } from 'react';
import { useAppUrlParams, UrlParamName } from 'hooks/navigation/useAppUrlParams';
import { contractClient } from 'shared/domain/contract/contractClient';
import { ContractModel } from 'shared/domain/contract/types/model';
import { useContractsStore } from 'store/contract';
import { useEntityById } from 'store/entityCollection';
import { EntityName } from 'shared/domain/entityName';

interface ContractContextType {
  contract: ContractModel | undefined;
  loading: boolean;
}

const ContractContext = React.createContext<ContractContextType | undefined>(undefined);

/** The contract of the URL; deleted contracts too. */
const WithContract: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  const id = useAppUrlParams(UrlParamName.CONTRACT_ID);
  const { items: contracts } = useContractsStore.use.data();
  const { item: contract, loading } = useEntityById({
    id,
    client: contractClient,
    items: contracts,
    entityName: EntityName.contracts,
  });

  const ctx = useMemo((): ContractContextType => ({ contract, loading }), [contract, loading]);

  return <ContractContext.Provider value={ctx}>{children}</ContractContext.Provider>;
};

export function useContract(): ContractContextType {
  const context = React.useContext(ContractContext);
  if (context === undefined) {
    throw new Error('useContract must be used within an ContractContextProvider');
  }
  return context;
}

export const withContract =
  (Component: React.ComponentType) =>
  ({ ...props }): React.ReactElement => (
    <WithContract>
      <Component {...props} />
    </WithContract>
  );
